// src/components/InboundSplitDonut.tsx
import React, { useMemo, useState } from 'react';
import { Card, Empty } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useInbounds } from '../hooks/useInbounds';
import { TimeRangeLinks } from '../ui/TimeRangeLinks';

/** Диапазон времени */
type Period = 'day' | 'week' | 'month' | 'year' | 'all';
const D = 24 * 60 * 60 * 1000;

const PERIOD_LABEL: Record<Period, string> = {
  day: '(24 ч)',
  week: '(7 дн)',
  month: '(30 дн)',
  year: '(365 дн)',
  all: '(за всё время)',
};

// Подмножество эталонных статусов/цветов
const STATUS_META = [
  { key: 'ready',      label: 'Готово',     color: '#1677ff' }, // blue
  { key: 'processing', label: 'В процессе', color: '#faad14' }, // gold
  { key: 'shipped',    label: 'На пути',    color: '#13c2c2' }, // cyan
] as const;

const toPeriod = (d?: number): Period =>
  d === 1 ? 'day' : d === 7 ? 'week' : d === 30 ? 'month' : d === 365 ? 'year' : 'month';

type Props = {
  height?: number;
  rangeDays?: number;
  // будущая синхронизация с таблицей (опционально, не ломает текущие вызовы)
  source?: any[];
  period?: Period;
  onPeriodChange?: (p: Period) => void;
};

const rangeFor = (p: Period) => {
  const now = Date.now();
  switch (p) {
    case 'day':  return { from: now - 1 * D, to: now };
    case 'week': return { from: now - 7 * D, to: now };
    case 'month':return { from: now - 30 * D, to: now };
    case 'year': return { from: now - 365 * D, to: now };
    case 'all':  return { from: 0, to: now };
  }
};

const InboundSplitDonut: React.FC<Props> = ({ height = 420, rangeDays, source, period, onPeriodChange }) => {
  const hook = useInbounds(10_000);
  const controlled = period !== undefined;

  const [innerPeriod, setInnerPeriod] = useState<Period>(toPeriod(rangeDays));
  const p = controlled ? (period as Period) : innerPeriod;
  const setP = (val: Period) => (controlled ? onPeriodChange?.(val) : setInnerPeriod(val));

  // Нормализуем произвольные статусы к эталонным ключам
  const pickStatus = (ib: any, idx: number): typeof STATUS_META[number]['key'] => {
    const s = (ib?.status ?? ib?.state ?? ib?.progress ?? '').toString().toLowerCase();
    if (s === 'ready') return 'ready';
    if (s === 'processing' || s === 'in_progress' || s === 'process') return 'processing';
    if (s === 'in_transit' || s === 'in-transit' || s === 'transit' || s === 'shipped' || s === 'on_the_way' || s === 'on-the-way') return 'shipped';
    // фолбэк: равномерно, чтобы мок-данные не давали пустой график
    return (['ready', 'processing', 'shipped'] as const)[idx % 3];
  };

  // Если передали source — используем его; иначе берём из хука
  const items = source ?? hook.items;
  const { from, to } = rangeFor(p);

  const { data, total } = useMemo(() => {
    // считаем КОЛ-ВО ТОВАРНЫХ ПОЗИЦИЙ (строк таблицы), а не количество поступлений
    const bucket: Record<string, number> = {};
    let total = 0;

    for (let i = 0; i < (items?.length ?? 0); i += 1) {
      const ib = items[i];
      const t = new Date(ib.createdAt).getTime();
      if (!Number.isFinite(t) || t < from || t >= to) continue;

      const key = pickStatus(ib, i);
      const lines: any[] = Array.isArray((ib as any).lines) ? (ib as any).lines : [];

      // считаем строки — как в InboundTable: если нет позиций, считаем 1 строку
      const linesCount = lines.length || 1;

      total += linesCount;
      bucket[key] = (bucket[key] || 0) + linesCount;
    }

    const rows = STATUS_META.map(s => ({
      key: s.key,
      name: s.label,
      value: bucket[s.key as string] || 0,
      color: s.color,
    }));

    const filtered = rows.filter(r => r.value > 0);
    const out = { data: filtered.length ? filtered : rows, total };

    if (process.env.NODE_ENV !== 'production') {
      const segmentsSum = rows.reduce((a, r) => a + r.value, 0);
      if (Math.abs(segmentsSum - total) > 0.001) {
        // eslint-disable-next-line no-console
        console.warn('InboundSplitDonut: сумма сегментов != total', { total, segmentsSum });
      }
    }

    return out;
  }, [items, from, to ]);


  const fmtPct = (f: number) =>
    new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(f * 100) + '%';

  const Tip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const pct = total ? fmtPct(p.value / total) : '0%';
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #eee',
          borderRadius: 8,
          padding: '8px 10px',
          boxShadow: '0 4px 16px rgba(0,0,0,.06)',
        }}
      >
        <div style={{ fontWeight: 600 }}>{p.name}</div>
        <div>{p.value} шт · {pct}</div>
      </div>
    );
  };

  return (
    <Card
      className="stat-card stat-card--accent-blue"
      styles={{ header: { paddingTop: 10, paddingBottom: 8, alignItems: 'center' } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="stat-card__icon" style={{ display: 'grid', placeItems: 'center' }}>
            <InboxOutlined />
          </span>
          <span className="stat-card__title">Структура поступлений</span>
          <span className="stat-card__suffix">{PERIOD_LABEL[p]}</span>
        </div>
      }
      extra={<TimeRangeLinks value={p} onChange={setP} withAll />}
    >
      <div style={{ width: '100%', height }}>
        {total === 0 ? (
          <Empty description="Нет данных" />
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Tooltip content={<Tip />} />
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={110}
                outerRadius={170}
                labelLine
                label={({ name, value }) => (value ? `${name}: ${fmtPct(total ? (value as number) / total : 0)}` : '')}
              >
                {data.map((r: any, i: number) => <Cell key={i} fill={r.color} />)}
              </Pie>

              {/* центр */}
              <text x="50%" y="50%" dy={-10} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 12, fill: '#667085' }}>
                Всего товаров
              </text>
              <text x="50%" y="50%" dy={12} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 18, fontWeight: 700 }}>
                {total} шт
              </text>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default InboundSplitDonut;
